import { SyncOperation, HistoricalSyncOptions, WebhookListResponse, LogsResponse } from '../types';

const API_BASE = '/api';

class ApiService {
  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      headers['X-API-Key'] = import.meta.env.VITE_ADMIN_API_KEY || '';
    }

    return headers;
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      headers: {
        ...this.getAuthHeaders(),
        ...options?.headers,
      },
      ...options,
    });

    if (!response.ok) {
      // If unauthorized and we have a token, it might be expired
      if (response.status === 401) {
        const token = localStorage.getItem('auth_token');
        if (token) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          window.location.href = '/login';
          return Promise.reject(new Error('Session expired. Please log in again.'));
        }
      }
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // Sync operations
  async startHistoricalSync(options: HistoricalSyncOptions, accountName?: string): Promise<{ syncId: string }> {
    if (accountName !== '') {
      return this.request(`/admin/sync/historical/${accountName}`, {
        method: 'POST',
        body: JSON.stringify(options),
      });
    } else {
      return this.request('/admin/sync/historical', {
        method: 'POST',
        body: JSON.stringify(options),
      });
    }
  }

  async getSyncStatus(syncId: string): Promise<SyncOperation> {
    return this.request(`/admin/sync/status/${syncId}`);
  }

  async listSyncOperations(): Promise<SyncOperation[]> {
    const response = await this.request<{ operations: SyncOperation[], total: number }>('/admin/sync/list');
    return response.operations;
  }

  async cancelSync(syncId: string): Promise<void> {
    await this.request(`/admin/sync/cancel?id=${syncId}`, {
      method: 'POST',
    });
  }

  // Webhook operations
  async listWebhooks(): Promise<WebhookListResponse> {
    return this.request('/admin/webhooks/list');
  }

  async setupWebhooks(webhookUrl: string, eventTypes?: string[]): Promise<void> {
    await this.request('/admin/webhooks/setup', {
      method: 'POST',
      body: JSON.stringify({
        webhookUrl,
        eventTypes,
      }),
    });
  }

  async removeWebhooks(webhookUrl: string): Promise<void> {
    await this.request(`/admin/webhooks/remove?url=${encodeURIComponent(webhookUrl)}`, {
      method: 'DELETE',
    });
  }

  async recreateWebhooks(webhookUrl: string, eventTypes?: string[]): Promise<void> {
    await this.request('/admin/webhooks/recreate', {
      method: 'POST',
      body: JSON.stringify({
        webhookUrl,
        eventTypes,
      }),
    });
  }

  // Log operations
  async getErrorLogs(): Promise<LogsResponse> {
    return this.request('/admin/logs');
  }

  async clearErrorLogs(): Promise<void> {
    await this.request('/admin/logs/clear', {
      method: 'DELETE',
    });
  }

  async generateTestLogs(): Promise<void> {
    await this.request('/admin/logs/generate-test', {
      method: 'POST',
    });
  }

  // Health check
  async getHealthStatus(): Promise<{ status: string; timestamp: string }> {
    return this.request('/health');
  }
}

export const apiService = new ApiService();
